import type { ActivityDefinition, PlanDefinition } from '@medplum/fhirtypes';

import { Icon, Text, cn } from '@eluve/components';
import {
  PLAN_SHAPES,
  type PlanGoal,
  type PlanShapeCode,
  type PlanSpec,
  fhirToSpec,
} from '@eluve/plan-shapes';

import { ControlBand } from './ControlBand';
import { FlareSwimlane } from './FlareSwimlane';
import { MarkdownProse } from './MarkdownProse';
import { PhaseRibbon } from './PhaseRibbon';
import { ACCENTS, type Accent, type AccentName } from './accents';
import { formatTargetValue } from './format';
import { IconTile, MonoChip, Panel, SectionHeader } from './panel';

// The render half of the Plan Shape Registry: each shape's one deterministic
// render primitive, dispatched on the spec's discriminator. The adapters
// (compile/decode) live in @eluve/plan-shapes; the pixels live here.

// Each shape's accent: episodic = orange, continuous = positive, phased = teal
// (the progression scale's end-state hue; individual phases run the whole scale).
const SHAPE_ACCENT_NAME: Record<PlanShapeCode, AccentName> = {
  phased: 'teal',
  episodic: 'orange',
  continuous: 'positive',
};

export function shapeAccent(shape: PlanShapeCode): Accent {
  return ACCENTS[SHAPE_ACCENT_NAME[shape]];
}

export function PlanShapeBadge({
  shape,
  className,
}: {
  shape: PlanShapeCode | undefined;
  className?: string;
}) {
  if (!shape) {
    return (
      <Text inline color="secondary">
        —
      </Text>
    );
  }
  const accent = shapeAccent(shape);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium',
        accent.tile,
        className,
      )}
    >
      <span className={cn('size-1.5 rounded-full', accent.fill)} />
      {PLAN_SHAPES[shape].label}
    </span>
  );
}

// Section copy per shape, used by the detail page and the builder preview.
export const SHAPE_SECTION_COPY: Record<
  PlanShapeCode,
  { heading: string; sub: string }
> = {
  phased: {
    heading: 'Phases',
    sub: 'The ordered phase structure of this plan.',
  },
  episodic: {
    heading: 'Baseline & events',
    sub: 'The standing care, and the events this plan defines a response to.',
  },
  continuous: {
    heading: 'Control bands',
    sub: 'The metrics this plan holds in range, and its monitoring rhythm.',
  },
};

// A Plan Spec's spine through its shape's render primitive, plus the shared
// goals layer. Renders a spec directly (the builder's living draft) — stored
// plans go through PlanShapeSection below, which decodes first.
export function PlanSpecView({ spec }: { spec: PlanSpec }) {
  const accent = shapeAccent(spec.shape);
  return (
    <div className="space-y-6">
      {spec.shape === 'phased' ? (
        <PhaseRibbon phases={spec.phases} />
      ) : spec.shape === 'episodic' ? (
        <FlareSwimlane
          baseline={spec.baseline}
          baselineActivities={spec.baselineActivities}
          events={spec.events}
          accent={accent}
        />
      ) : (
        <ControlBand
          bands={spec.bands}
          cadence={spec.cadence}
          reviewRule={spec.reviewRule}
          activities={spec.activities}
          accent={accent}
        />
      )}
      {spec.goals && spec.goals.length > 0 && (
        <GoalList goals={spec.goals} accent={accent} />
      )}
    </div>
  );
}

function GoalList({ goals, accent }: { goals: PlanGoal[]; accent: Accent }) {
  return (
    <div>
      <SectionHeader title="Goals" barClassName={accent.fill} />
      <ul className="mt-3 space-y-3">
        {goals.map((goal, i) => (
          <li key={i}>
            <Panel className="flex items-start gap-3 px-4 py-3.5">
              <IconTile className={accent.tile}>
                <Icon name="Target" size="auto" />
              </IconTile>
              <div className="min-w-0">
                <MarkdownProse
                  content={goal.description}
                  className="text-sm font-medium text-foreground"
                />
                {goal.targets && goal.targets.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {goal.targets.map((target, j) => (
                      <MonoChip key={j}>
                        {target.measure.text} · {formatTargetValue(target)}
                      </MonoChip>
                    ))}
                  </div>
                )}
              </div>
            </Panel>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Registry dispatch for a stored plan: decode → shape primitive, with a
// graceful fallback for plans that match no known shape (e.g. Synthea-seeded
// PlanDefinitions). Pass the plan's resolved ActivityDefinitions to recover
// exercise-intrinsic fields on its activity entries.
export function PlanShapeSection({
  plan,
  activityDefinitions,
}: {
  plan: PlanDefinition;
  activityDefinitions?: ActivityDefinition[];
}) {
  const spec = fhirToSpec(plan, { activityDefinitions });
  if (!spec) {
    return (
      <div className="py-10 text-sm text-muted-foreground">
        This plan doesn't match a known Plan Shape, so it has no structured
        view.
      </div>
    );
  }
  const copy = SHAPE_SECTION_COPY[spec.shape];
  return (
    <>
      <div className="border-t border-border pt-6">
        <SectionHeader
          title={copy.heading}
          description={copy.sub}
          barClassName={shapeAccent(spec.shape).fill}
        />
      </div>
      <div className="py-6">
        <PlanSpecView spec={spec} />
      </div>
    </>
  );
}
